import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { fieldSx } from "../../egresos/components/formStyles";
import {
  createReglaDetalle,
  deleteReglaDetalle,
  getFacItems,
  getReglasDetalleAdmin,
  getServiciosFacturacion,
  updateReglaDetalle,
} from "../services/facturacionService";

const emptyForm = {
  id: null,
  cod_servicio: "",
  categoria: "servicio",
  id_fac_item: "",
  porcentaje: "100",
  detalle_hippo: "",
  descripcion_interna: "",
  metodo_calculo: "",
  orden: "1",
  estado: "A",
};

const ESTADOS_REGLA = {
  A: { texto: "Activa", color: "success" },
  I: { texto: "Inactiva", color: "warning" },
  X: { texto: "Eliminada", color: "default" },
};

const getErrorMessage = (error, fallback = "Ocurrio un error") =>
  error?.response?.data?.message || error?.message || fallback;

const pct = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtPct = (n) => `${Number.isInteger(n) ? n : Number(n.toFixed(2))} %`;

const Ayuda = ({ children }) => (
  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.3, mt: 0.25 }}>{children}</Typography>
);

// Una "regla interna" es una línea de un concepto. Las líneas con el mismo servicio y el mismo método de cálculo forman un concepto,
// y sus porcentajes (solo las activas) deben sumar 100 %.
const agruparConceptos = (reglas) => {
  const mapa = new Map();
  reglas.forEach((r) => {
    const clave = `${r.cod_servicio}|${r.metodo_calculo}`;
    if (!mapa.has(clave)) {
      mapa.set(clave, { clave, cod_servicio: r.cod_servicio, servicio: r.servicio_nombre || String(r.cod_servicio), metodo: r.metodo_calculo, descripcion: r.descripcion_interna, lineas: [] });
    }
    const c = mapa.get(clave);
    if (!c.descripcion && r.descripcion_interna) c.descripcion = r.descripcion_interna;
    c.lineas.push(r);
  });
  return [...mapa.values()].map((c) => {
    c.lineas.sort((a, b) => Number(a.orden) - Number(b.orden) || a.id - b.id);
    c.total = c.lineas.filter((l) => l.estado === "A").reduce((a, l) => a + pct(l.porcentaje), 0);
    c.cuadra = Math.abs(c.total - 100) < 0.005;
    c.hayActivas = c.lineas.some((l) => l.estado === "A");
    return c;
  }).sort((a, b) => String(a.servicio).localeCompare(String(b.servicio)) || String(a.metodo).localeCompare(String(b.metodo)));
};

const ReglasDetalleTab = () => {
  const [servicios, setServicios] = useState([]);
  const [items, setItems] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [filters, setFilters] = useState({ cod_servicio: "Todos", buscar: "", estado: "A" });
  const [form, setForm] = useState(emptyForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const selectedServicio = useMemo(
    () => servicios.find((s) => String(s.id_servicio) === String(form.cod_servicio)),
    [servicios, form.cod_servicio]
  );

  const loadCatalogos = async () => {
    try {
      const res = await getServiciosFacturacion();
      setServicios(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(getErrorMessage(e, "No se pudieron cargar los servicios"));
    }
  };

  const loadItems = async (codServicio) => {
    if (!codServicio) {
      setItems([]);
      return;
    }
    try {
      const res = await getFacItems({ cod_servicio: codServicio });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(getErrorMessage(e, "No se pudieron cargar los items"));
    }
  };

  const loadReglas = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        cod_servicio: filters.cod_servicio === "Todos" ? "" : filters.cod_servicio,
      };
      const res = await getReglasDetalleAdmin(params);
      setReglas(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(getErrorMessage(e, "No se pudieron cargar las reglas"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalogos();
  }, []);

  useEffect(() => {
    loadReglas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.cod_servicio, filters.estado]);

  useEffect(() => {
    loadItems(form.cod_servicio);
  }, [form.cod_servicio]);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const updateForm = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setItems([]);
  };

  const openNewDialog = () => {
    setMessage(null);
    setError(null);
    resetForm();
    setDialogOpen(true);
  };

  // Nueva línea dentro de un concepto que ya existe: se hereda servicio, método y descripción; el % sugerido es lo que falta para 100
  const openAddLinea = (concepto) => {
    setMessage(null);
    setError(null);
    const maxOrden = Math.max(0, ...concepto.lineas.map((l) => Number(l.orden) || 0));
    const falta = Math.max(100 - concepto.total, 0);
    setForm({
      ...emptyForm,
      cod_servicio: concepto.cod_servicio,
      metodo_calculo: concepto.metodo,
      descripcion_interna: concepto.descripcion || "",
      categoria: "",
      orden: String(maxOrden + 1),
      porcentaje: String(falta || ""),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    resetForm();
  };

  const editRegla = (regla) => {
    setMessage(null);
    setError(null);
    setForm({
      id: regla.id,
      cod_servicio: regla.cod_servicio || "",
      categoria: regla.categoria || "servicio",
      id_fac_item: regla.id_fac_item || "",
      porcentaje: regla.porcentaje ?? "100",
      detalle_hippo: regla.detalle_hippo || "",
      descripcion_interna: regla.descripcion_interna || "",
      metodo_calculo: regla.metodo_calculo || "",
      orden: regla.orden || "1",
      estado: regla.estado || "A",
    });
    setDialogOpen(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        ...form,
        cod_servicio: Number(form.cod_servicio),
        id_fac_item: Number(form.id_fac_item),
        porcentaje: Number(form.porcentaje),
        orden: Number(form.orden || 1),
      };
      if (form.id) await updateReglaDetalle(form.id, payload);
      else await createReglaDetalle(payload);
      setMessage(form.id ? "Línea actualizada" : "Línea creada");
      setDialogOpen(false);
      resetForm();
      await loadReglas();
    } catch (e) {
      setError(getErrorMessage(e, "No se pudo guardar la regla"));
    } finally {
      setSaving(false);
    }
  };

  const desactivar = async (regla) => {
    if (!window.confirm(`¿Desactivar la línea “${regla.detalle_hippo}” (${regla.porcentaje}%) del concepto ${regla.metodo_calculo}?`)) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await deleteReglaDetalle(regla.id);
      setMessage("Línea desactivada");
      await loadReglas();
      if (String(form.id) === String(regla.id)) resetForm();
    } catch (e) {
      setError(getErrorMessage(e, "No se pudo desactivar la regla"));
    } finally {
      setSaving(false);
    }
  };

  // ---- datos para mostrar ----
  const conceptos = useMemo(() => agruparConceptos(reglas), [reglas]);
  const sinCuadrar = conceptos.filter((c) => c.hayActivas && !c.cuadra).length;
  const porServicio = useMemo(() => {
    const m = new Map();
    conceptos.forEach((c) => { if (!m.has(c.servicio)) m.set(c.servicio, []); m.get(c.servicio).push(c); });
    return [...m.entries()];
  }, [conceptos]);

  // Suma que quedaría en el concepto con la línea del formulario (solo si queda activa)
  const sumaConFormulario = useMemo(() => {
    if (!form.cod_servicio || !form.metodo_calculo) return null;
    const c = conceptos.find((x) => String(x.cod_servicio) === String(form.cod_servicio) && x.metodo === form.metodo_calculo);
    const otras = (c?.lineas || []).filter((l) => l.estado === "A" && l.id !== form.id).reduce((a, l) => a + pct(l.porcentaje), 0);
    return otras + (form.estado === "A" ? pct(form.porcentaje) : 0);
  }, [conceptos, form]);

  return (
    <Box sx={{ p: 1 }}>
      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity="success" variant="filled" onClose={() => setMessage(null)} sx={{ width: "100%" }}>{message}</Alert>
      </Snackbar>

      {/* ── Qué es esta pantalla ── */}
      <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 1, py: 0.25, fontSize: "0.8rem", alignItems: "center" }}>
        <b>¿Cómo funciona?</b> Un <b>concepto</b> (por ejemplo <b>AVL_NACIONAL</b>) es lo que se elige en una pre-factura con “Añadir concepto”. Al facturar,
        su precio se reparte en una o más <b>líneas fiscales para Hippo</b> según los porcentajes (ej.: 30 % servicio + 70 % alquiler).
        Los porcentajes de las líneas activas de cada concepto deben <b>sumar 100 %</b>.
      </Alert>

      {/* ── Filtros ── */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TextField select size="small" label="Servicio" value={filters.cod_servicio} onChange={(e) => updateFilter("cod_servicio", e.target.value)} sx={{ ...fieldSx, width: 190 }}>
          <MenuItem value="Todos">Todos</MenuItem>
          {servicios.map((s) => (
            <MenuItem key={s.id_servicio} value={s.id_servicio}>{s.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Estado de las líneas" value={filters.estado} onChange={(e) => updateFilter("estado", e.target.value)} sx={{ ...fieldSx, width: 170 }}>
          <MenuItem value="Todos">Todas</MenuItem>
          <MenuItem value="A">Activas</MenuItem>
          <MenuItem value="I">Inactivas</MenuItem>
          <MenuItem value="X">Eliminadas</MenuItem>
        </TextField>
        <TextField
          size="small" placeholder="Buscar concepto, detalle o item…" value={filters.buscar} onChange={(e) => updateFilter("buscar", e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadReglas()}
          sx={{ ...fieldSx, width: { xs: "100%", sm: 280 } }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
        />
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadReglas} disabled={loading} sx={{ textTransform: "none" }}>Buscar</Button>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {conceptos.length} concepto{conceptos.length === 1 ? "" : "s"} · {reglas.length} línea{reglas.length === 1 ? "" : "s"}
        </Typography>
        {sinCuadrar > 0 && (
          <Chip size="small" color="error" icon={<WarningAmberIcon />} label={`${sinCuadrar} no suma${sinCuadrar === 1 ? "" : "n"} 100 %`} sx={{ fontWeight: 700 }} />
        )}
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNewDialog} sx={{ textTransform: "none", fontWeight: 700 }}>Nuevo concepto</Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Conceptos agrupados por servicio ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} /></Box>
      ) : conceptos.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No hay reglas para mostrar con estos filtros.</Paper>
      ) : porServicio.map(([servicio, lista]) => (
        <Box key={servicio} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.main", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5, borderBottom: 1, borderColor: "divider", pb: 0.25 }}>
            {servicio} <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 400, color: "text.secondary", textTransform: "none", letterSpacing: 0 }}>· {lista.length} concepto{lista.length === 1 ? "" : "s"}</Typography>
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1 }}>
            {lista.map((c) => (
              <Paper key={c.clave} variant="outlined" sx={{ overflow: "hidden", borderColor: c.hayActivas && !c.cuadra ? "error.main" : "divider" }}>
                {/* Encabezado del concepto */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, bgcolor: (t) => alpha(t.palette.text.primary, 0.04), borderBottom: 1, borderColor: "divider" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, lineHeight: 1.25, wordBreak: "break-word" }}>{c.metodo}</Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.25 }}>{c.descripcion || "Sin descripción interna"}</Typography>
                  </Box>
                  {c.hayActivas ? (
                    c.cuadra
                      ? <Chip size="small" color="success" variant="outlined" icon={<CheckCircleOutlineIcon />} label="Suma 100 %" sx={{ height: 22, fontWeight: 700, fontSize: 11.5 }} />
                      : <Tooltip title="Los porcentajes de las líneas activas deben sumar 100 %. Agrega o ajusta una línea."><Chip size="small" color="error" icon={<WarningAmberIcon />} label={`Suma ${fmtPct(c.total)}`} sx={{ height: 22, fontWeight: 700, fontSize: 11.5 }} /></Tooltip>
                  ) : (
                    <Chip size="small" variant="outlined" label="Sin líneas activas" sx={{ height: 22, fontSize: 11.5 }} />
                  )}
                </Box>

                {/* Líneas fiscales del concepto */}
                {c.lineas.map((l) => {
                  const est = ESTADOS_REGLA[l.estado] || { texto: l.estado, color: "default" };
                  const activa = l.estado === "A";
                  return (
                    <Box key={l.id} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.6, borderBottom: 1, borderColor: "divider", opacity: activa ? 1 : 0.6 }}>
                      <Typography sx={{ width: 16, fontSize: 11, color: "text.disabled", textAlign: "center" }} title="Orden">{l.orden}</Typography>
                      <Chip size="small" color="primary" label={fmtPct(pct(l.porcentaje))} sx={{ height: 22, minWidth: 54, fontWeight: 800, fontSize: 11.5 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, lineHeight: 1.25 }}>{l.detalle_hippo}</Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.25 }}>
                          Item: {l.item_nombre || l.id_fac_item}{l.categoria ? ` · ${l.categoria}` : ""}
                        </Typography>
                      </Box>
                      {!activa && <Chip size="small" variant="outlined" color={est.color} label={est.texto} sx={{ height: 18, fontSize: 10.5 }} />}
                      <Tooltip title="Editar línea"><IconButton size="small" onClick={() => editRegla(l)}><EditIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                      <Tooltip title="Desactivar línea">
                        <span>
                          <IconButton size="small" color="error" onClick={() => desactivar(l)} disabled={l.estado === "X" || saving}><DeleteIcon sx={{ fontSize: 17 }} /></IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}

                <Box sx={{ px: 1, py: 0.25 }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => openAddLinea(c)} sx={{ textTransform: "none", fontSize: "0.74rem" }}>
                    Agregar línea a este concepto
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      ))}

      {/* ── Crear / editar una línea ── */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          {form.id ? "Editar línea del concepto" : form.metodo_calculo && form.cod_servicio ? "Nueva línea del concepto" : "Nuevo concepto"}
          {form.metodo_calculo && <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: "text.secondary" }}>{form.metodo_calculo}</Typography>}
        </DialogTitle>
        <Box component="form" onSubmit={submit}>
          <DialogContent dividers sx={{ p: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.main", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>Concepto interno</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1, mb: 1.5 }}>
              <TextField select size="small" label="Servicio" value={form.cod_servicio} onChange={(e) => updateForm("cod_servicio", e.target.value)} sx={fieldSx} required>
                {servicios.map((s) => (
                  <MenuItem key={s.id_servicio} value={s.id_servicio}>{s.nombre}</MenuItem>
                ))}
              </TextField>
              <Box>
                <TextField fullWidth size="small" label="Nombre del concepto" value={form.metodo_calculo} onChange={(e) => updateForm("metodo_calculo", e.target.value.toUpperCase())} sx={fieldSx} required />
                <Ayuda>Es el nombre que se elige en la pre-factura. Ej.: AVL_NACIONAL.</Ayuda>
              </Box>
              <Box>
                <TextField fullWidth size="small" label="Descripción interna" value={form.descripcion_interna} onChange={(e) => updateForm("descripcion_interna", e.target.value)} sx={fieldSx} />
                <Ayuda>Texto de uso interno que se muestra al elegir el concepto.</Ayuda>
              </Box>
            </Box>

            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.main", textTransform: "uppercase", letterSpacing: 0.4, mb: 0.75 }}>Línea fiscal para Hippo</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.5fr 1.5fr 0.8fr" }, gap: 1, mb: 1 }}>
              <Box>
                <TextField select fullWidth size="small" label={selectedServicio ? `Item de facturación (${selectedServicio.nombre})` : "Item de facturación"} value={form.id_fac_item} onChange={(e) => updateForm("id_fac_item", e.target.value)} sx={fieldSx} required disabled={!form.cod_servicio}>
                  {items.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.item} ({item.cod_producto_servicio})
                    </MenuItem>
                  ))}
                </TextField>
                <Ayuda>Define el producto/servicio del SIN que se factura.</Ayuda>
              </Box>
              <Box>
                <TextField fullWidth size="small" label="Detalle que sale en la factura" value={form.detalle_hippo} onChange={(e) => updateForm("detalle_hippo", e.target.value)} sx={fieldSx} required />
                <Ayuda>Texto de la línea en la factura electrónica (Hippo).</Ayuda>
              </Box>
              <Box>
                <TextField fullWidth size="small" label="Categoría" value={form.categoria} onChange={(e) => updateForm("categoria", e.target.value)} sx={fieldSx} />
                <Ayuda>Ej.: servicio, alquiler.</Ayuda>
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 1 }}>
              <Box>
                <TextField fullWidth size="small" label="Porcentaje del precio" value={form.porcentaje} onChange={(e) => updateForm("porcentaje", e.target.value.replace(",", "."))} sx={fieldSx} required
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }} />
                <Ayuda>Qué parte del precio del concepto va en esta línea.</Ayuda>
              </Box>
              <Box>
                <TextField fullWidth size="small" label="Orden" value={form.orden} onChange={(e) => updateForm("orden", e.target.value)} sx={fieldSx} required />
                <Ayuda>Posición de la línea en la factura (1 sale primero).</Ayuda>
              </Box>
              <TextField select size="small" label="Estado" value={form.estado} onChange={(e) => updateForm("estado", e.target.value)} sx={fieldSx}>
                <MenuItem value="A">Activa</MenuItem>
                <MenuItem value="I">Inactiva</MenuItem>
                <MenuItem value="X">Eliminada</MenuItem>
              </TextField>
            </Box>

            {sumaConFormulario !== null && (
              <Alert severity={Math.abs(sumaConFormulario - 100) < 0.005 ? "success" : "warning"} icon={false} sx={{ mt: 1.5, py: 0, fontSize: 12.5 }}>
                Con esta línea, el concepto <b>{form.metodo_calculo}</b> suma <b>{fmtPct(sumaConFormulario)}</b>
                {Math.abs(sumaConFormulario - 100) < 0.005 ? " ✓" : ". Debe sumar 100 % entre todas sus líneas activas."}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button size="small" variant="outlined" color="inherit" onClick={closeDialog} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} disabled={saving}>
              {form.id ? "Actualizar" : "Guardar"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ReglasDetalleTab;
